import { jsons2csv } from 'react-csv/lib/core';
import * as XLSX from 'xlsx/xlsx';
import {
  ReconciliationItemMetricComparison,
  ReconciliationRunDetail,
  ReconciliationRunExportData,
  ReconciliationRunExportItem,
} from '../types/reconciliation';

/** One flattened row per item × comparison (matches export API + CSV spec). */
export type ReconciliationExportCsvRow = {
  runId: number;
  orderId: string;
  runType: string;
  runStatus: string;
  executionFile: string;
  runDate: string;
  userId: string;
  fundId: string;
  fundName: string;
  itemStatus: string;
  step: string;
  metric: string;
  passFail: string;
  systemValue: string;
  fileValue: string;
  delta: string;
  message: string;
};

/** Header order for CSV / Excel (labels match product copy). */
export const RECON_CSV_HEADERS: { label: string; key: keyof ReconciliationExportCsvRow }[] = [
  { label: 'Run ID', key: 'runId' },
  { label: 'Order ID', key: 'orderId' },
  { label: 'Run Type', key: 'runType' },
  { label: 'Run Status', key: 'runStatus' },
  { label: 'Execution File', key: 'executionFile' },
  { label: 'Run Date', key: 'runDate' },
  { label: 'User ID', key: 'userId' },
  { label: 'Fund ID', key: 'fundId' },
  { label: 'Fund Name', key: 'fundName' },
  { label: 'Item Status', key: 'itemStatus' },
  { label: 'Step', key: 'step' },
  { label: 'Metric', key: 'metric' },
  { label: 'Pass/Fail', key: 'passFail' },
  { label: 'System Value', key: 'systemValue' },
  { label: 'File Value', key: 'fileValue' },
  { label: 'Delta', key: 'delta' },
  { label: 'Message', key: 'message' },
];

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** YYYY-MM-DD for filename (UTC date from ISO timestamp). */
export function reconciliationRunDatePart(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return 'unknown-date';
  }
  return d.toISOString().slice(0, 10);
}

export function reconciliationRunTypeSlug(runType: string): string {
  const s = runType
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return s || 'run';
}

export function reconciliationExportFilename(run: ReconciliationRunDetail, ext: 'csv' | 'xlsx'): string {
  const date = reconciliationRunDatePart(run.createdAt);
  const type = reconciliationRunTypeSlug(run.runType);
  return `recon_${date}_${type}.${ext}`;
}

function cellString(v: string | number | boolean | null | undefined): string {
  if (v == null) {
    return '';
  }
  if (typeof v === 'boolean') {
    return v ? 'TRUE' : 'FALSE';
  }
  return String(v);
}

function systemValueCell(m: ReconciliationItemMetricComparison): string {
  return cellString(m.systemValue ?? m.system_value);
}

function fileValueCell(m: ReconciliationItemMetricComparison): string {
  return cellString(m.fileValue ?? m.file_value);
}

function metricLabel(m: ReconciliationItemMetricComparison): string {
  return String(m.metricName ?? m.metric_name ?? '').trim();
}

function deltaCell(m: ReconciliationItemMetricComparison): string {
  if (m.delta == null || m.delta === '') {
    return '';
  }
  return String(m.delta);
}

/** When API omits flags, infer from displayed system vs file values. */
function inferredMatchFromValues(m: ReconciliationItemMetricComparison): boolean {
  const sv = systemValueCell(m).trim();
  const fv = fileValueCell(m).trim();
  if (sv === fv) {
    return true;
  }
  const sn = Number(sv);
  const fn = Number(fv);
  if (Number.isFinite(sn) && Number.isFinite(fn) && sv !== '' && fv !== '') {
    return sn === fn;
  }
  return false;
}

function passFailLabel(m: ReconciliationItemMetricComparison): 'PASS' | 'FAIL' {
  const primary = m.isMatch;
  if (typeof primary === 'boolean') {
    return primary ? 'PASS' : 'FAIL';
  }
  if (typeof primary === 'number') {
    return primary !== 0 ? 'PASS' : 'FAIL';
  }

  const legacy = m.match;
  if (typeof legacy === 'boolean') {
    return legacy ? 'PASS' : 'FAIL';
  }
  if (typeof legacy === 'number') {
    return legacy !== 0 ? 'PASS' : 'FAIL';
  }
  if (typeof legacy === 'string') {
    const u = legacy.trim().toUpperCase();
    if (u === '') {
      return inferredMatchFromValues(m) ? 'PASS' : 'FAIL';
    }
    if (u === 'TRUE' || u === '1' || u === 'YES' || u === 'MATCH' || u === 'PASS') {
      return 'PASS';
    }
    if (u === 'FALSE' || u === '0' || u === 'NO' || u === 'FAIL') {
      return 'FAIL';
    }
    return 'FAIL';
  }

  return inferredMatchFromValues(m) ? 'PASS' : 'FAIL';
}

function itemComparisons(item: ReconciliationRunExportItem): ReconciliationItemMetricComparison[] {
  return item.comparisons ?? item.metrics ?? item.metricComparisons ?? [];
}

export function buildReconciliationExportRows(
  run: ReconciliationRunDetail,
  items: ReconciliationRunExportItem[],
): ReconciliationExportCsvRow[] {
  const rows: ReconciliationExportCsvRow[] = [];

  for (const item of items) {
    for (const check of itemComparisons(item)) {
      rows.push({
        runId: run.id,
        orderId: run.orderId,
        runType: run.runType,
        runStatus: run.status,
        executionFile: run.executionFileName ?? '',
        runDate: run.createdAt,
        userId: String(item.userId ?? ''),
        fundId: String(item.fundId ?? ''),
        fundName: item.fundName ?? '',
        itemStatus: item.itemStatus ?? '',
        step: check.step != null && check.step !== '' ? String(check.step) : '',
        metric: metricLabel(check),
        passFail: passFailLabel(check),
        systemValue: systemValueCell(check),
        fileValue: fileValueCell(check),
        delta: deltaCell(check),
        message: check.message ?? '',
      });
    }
  }

  return rows;
}

function aoaToAllStrings(aoa: string[][]): string[][] {
  return aoa.map((row) => row.map((cell) => (cell == null ? '' : String(cell))));
}

function applyColumnWidthsFromAoA(ws: XLSX.WorkSheet, aoa: string[][]) {
  if (aoa.length === 0) {
    return;
  }
  const colCount = Math.max(...aoa.map((row) => row.length), 0);
  const minWch = 12;
  const maxWch = 72;
  const maxLens = Array(colCount).fill(minWch);

  for (const row of aoa) {
    for (let c = 0; c < row.length; c += 1) {
      const len = row[c]?.length ?? 0;
      maxLens[c] = Math.max(maxLens[c], Math.min(len + 2, maxWch));
    }
  }

  ws['!cols'] = maxLens.map((wch) => ({ wch }));
}

/** CSV via react-csv core (same formatting as CSVLink). */
export function downloadReconciliationRunCsvFromJson(data: ReconciliationRunExportData) {
  const { run, items } = data;
  const exportRows = buildReconciliationExportRows(run, items);
  const csvBody = jsons2csv(exportRows, RECON_CSV_HEADERS, ',', '"');
  const csv = `\uFEFF${csvBody}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, reconciliationExportFilename(run, 'csv'));
}

/** XLSX: same columns as CSV, all string cells for stable Excel display. */
export function downloadReconciliationRunXlsxFromJson(data: ReconciliationRunExportData) {
  const { run, items } = data;
  const exportRows = buildReconciliationExportRows(run, items);
  const headerLabels = RECON_CSV_HEADERS.map((h) => h.label);
  const body = exportRows.map((row) => RECON_CSV_HEADERS.map((h) => String(row[h.key] ?? '')));
  const aoa = aoaToAllStrings([headerLabels, ...body]);
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  applyColumnWidthsFromAoA(sheet, aoa);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Reconciliation export');
  XLSX.writeFile(workbook, reconciliationExportFilename(run, 'xlsx'));
}
